import _ from 'lodash';
import numeral from 'numeral';
import {
  UPDATE_QUADRANT_I,
  UPDATE_QUADRANT_II,
  UPDATE_SITE,
  UPDATE_METADATA,
  TOGGLE_SLIDER
} from '../types';

import {
  reflectivity,
  velocity,
  spectrumWidth,
  differentialReflectivity,
  correlationCoefficient,
  specificDifferentialPhase,
  differentialPhaseShift,
  enhancedEchoTops,
  digitalVIL
} from '../RadarColors';

const getProduct = (product, site) => {
  let unit, paint, hash;
  switch (_.toLower(product)) {
    case 'dvl':
      hash = `${_.toLower(product)}_${site}`;
      paint = digitalVIL;
      unit = ' kg/m<sup>2</sup>';
      break;
    case 'eet':
      hash = `${_.toLower(product)}_${site}`;
      paint = enhancedEchoTops;
      unit = ' kft';
      break;
    case 'n0q':
    case 'n1q':
    case 'n2q':
    case 'n3q':
    case 'ref0':
    case 'ref1':
    case 'ref2':
    case 'ref3':
    case 'ref4':
    case 'ref5':
    case 'ref6':
    case 'ref7':
    case 'ref8':
    case 'ref9':
    case 'ref10':
    case 'ref11':
    case 'ref12':
      hash = `${_.toLower(product)}_${site}`;
      paint = reflectivity;
      unit = ' dBZ';
      break;
    case 'n0u':
    case 'n1u':
    case 'n2u':
    case 'n3u':
    case 'n0s':
    case 'n1s':
    case 'n2s':
    case 'n3s':
    case 'vel0':
    case 'vel1':
    case 'vel2':
    case 'vel3':
    case 'vel4':
    case 'vel5':
    case 'vel6':
    case 'vel7':
    case 'vel8':
    case 'vel9':
      hash = `${_.toLower(product)}_${site}`;
      paint = velocity;
      unit = ' kts';
      break;
    case 'nsp':
    case 'nsw':
    case 'sw0':
    case 'sw1':
    case 'sw2':
    case 'sw3':
    case 'sw4':
    case 'sw5':
    case 'sw6':
    case 'sw7':
    case 'sw8':
    case 'sw9':
      hash = `${_.toLower(product)}_${site}`;
      paint = spectrumWidth;
      unit = ' kts';
      break;
    case 'n0x':
    case 'nax':
    case 'n1x':
    case 'nbx':
    case 'n2x':
    case 'n3x':
    case 'dr0':
    case 'dr1':
    case 'dr2':
    case 'dr3':
    case 'dr4':
    case 'dr5':
    case 'dr6':
    case 'dr7':
    case 'dr8':
    case 'dr9':
      hash = `${_.toLower(product)}_${site}`;
      paint = differentialReflectivity;
      unit = ' dB';
      break;
    case 'n0c':
    case 'nac':
    case 'n1c':
    case 'nbc':
    case 'n2c':
    case 'n3c':
    case 'cc0':
    case 'cc1':
    case 'cc2':
    case 'cc3':
    case 'cc4':
    case 'cc5':
    case 'cc6':
    case 'cc7':
    case 'cc8':
    case 'cc9':
      hash = `${_.toLower(product)}_${site}`;
      paint = correlationCoefficient;
      unit = '';
      break;
    case 'n0k':
    case 'nak':
    case 'n1k':
    case 'nbk':
    case 'n2k':
    case 'n3k':
      hash = `${_.toLower(product)}_${site}`;
      paint = specificDifferentialPhase;
      unit = '&deg;/km';
      break;
    case 'dp0':
    case 'dp1':
    case 'dp2':
    case 'dp3':
    case 'dp4':
    case 'dp5':
    case 'dp6':
    case 'dp7':
    case 'dp8':
    case 'dp9':
      hash = `${_.toLower(product)}_${site}`;
      paint = differentialPhaseShift;
      unit = '&deg;';
      break;
  };
  return { unit, paint, hash };
}

const initialProductI = getProduct('ref0', 'KTLX');
const initialUnitsI = initialProductI.unit;
const initialColorI = initialProductI.paint;
const initialHashI = initialProductI.hash;
const initialProductII = getProduct('n0u', 'KTLX');
const initialUnitsII = initialProductII.unit;
const initialColorII = initialProductII.paint;
const initialHashII = initialProductII.hash;
const initialState = {
  quadrantI: {
    product: 'ref0',
    units: initialUnitsI,
    selectedColor: initialColorI,
    hash: initialHashI
  },
  quadrantII: {
    product: 'n0u',
    units: initialUnitsII,
    selectedColor: initialColorII,
    hash: initialHashII
  },
  site: 'KTLX',
  enableSlider: false,
  lat: null,
  lon: null,
  properties: {}
};

export default function(state = initialState, action) {
  switch (action.type) {
    case UPDATE_QUADRANT_I:
      const selectedProductI = getProduct(action.payload.product, state.site);
      const unitsI = selectedProductI.unit;
      const selectedColorI = selectedProductI.paint;
      const hashI = selectedProductI.hash;
      return { ...state, quadrantI: {
        product: action.payload.product,
        units: unitsI,
        selectedColor: selectedColorI,
        hash: hashI
      } };
    case UPDATE_QUADRANT_II:
      const selectedProductII = getProduct(action.payload.product, state.site);
      const unitsII = selectedProductII.unit;
      const selectedColorII = selectedProductII.paint;
      const hashII = selectedProductII.hash;
      return { ...state, quadrantII: {
        product: action.payload.product,
        units: unitsII,
        selectedColor: selectedColorII,
        hash: hashII
      } };
    case TOGGLE_SLIDER:
      console.log(state);
      return { ...state, enableSlider: !state.enableSlider };
    case UPDATE_SITE:
      return { ...state, site: action.payload };
    case UPDATE_METADATA:
      return {
        ...state,
        lat: action.payload.lat >= 0 ?
          `${numeral(action.payload.lat).format('0.0000')}\xB0 N` :
          `${numeral(-1 * action.payload.lat).format('0.0000')}\xB0 S`,
        lon: action.payload.lon >= 0 ?
          `${numeral(action.payload.lon).format('0.0000')}\xB0 E` :
          `${numeral(action.payload.lon).format('0.0000')}\xB0 W`,
        properties: {
          angle: action.payload.properties.elevAngle > 0 ?
            `${numeral(action.payload.properties.elevAngle).format('0.[00]')}\xB0` : null,
          elevation: action.payload.properties.heightRel > 0 ?
            `${numeral(action.payload.properties.heightRel).format('0.00 a')}m` : null,
          value: action.payload.properties.value || action.payload.properties.value == 0 ? action.payload.properties.value : null
        }
      };
    default:
      return { ...state };
  }
}